import { ReactInstance, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { printPdf } from '../../utils/htmlUtils';
import usePreviousValue from '../common/usePreviousValue';
import { usePrint } from '../print';
import { useAddReadingListArticle, useDefaultReadingList, useRemoveReadingListArticle } from '../readingList';
import { queryKeys } from '../queryKeyFactory';
import { useFetchLikes, useLikeArticle, useUnlikeArticle } from '../likes';
import { useToast } from '../toast/useToast';
import {
  containsPdfContent,
  downloadPdfContent,
  isPdfViewerSupported,
} from '../../pages/Article/ArticleContent/ArticleContent.logic';
import { ROUTES } from '../../Base.constants';
import copyToClipboard from '../../utils/clipboardUtils';
import { RemoveReadingListArticlePayload } from '../readingList/useRemoveReadingListArticle';
import { AddReadingListArticlePayload } from '../readingList/useAddReadingListArticle';
import { Channels } from '../../utils/channels';
import { ArticleContent, Tag } from '../../models';

interface useGetArticleActionsResult {
  onShare: () => Promise<void>;
  onBookmark: () => void;
  isBookmarkLoading: boolean;
  bookmarkId: string | undefined;
  onLike: () => void;
  isLikeLoading: boolean;
  likeId: string | null;
  isDownloadLoading: boolean;
  isPrintLoading: boolean;
  onDownload: () => void;
  onPrint: () => void;
}

interface useGetArticleActionsParams {
  id: number;
  title: string;
  tags: Tag[];
  publishDate: string;
  canAccess?: boolean;
  content?: ArticleContent;
  articlePrintRef?: ReactInstance | null;
}

const useGetArticleActions = ({
  id,
  title,
  content,
  tags,
  publishDate,
  articlePrintRef,
  canAccess,
}: useGetArticleActionsParams): useGetArticleActionsResult => {
  const queryClient = useQueryClient();
  const { data: defaultReadingList, isFetching: isDefaultReadingListLoading } = useDefaultReadingList({
    enabled: canAccess,
  });
  const { isLoading: isAddBookmarkLoading, mutate: addBookmark } = useAddReadingListArticle();
  const { isLoading: isRemoveBookmarkLoading, mutate: removeBookmark } = useRemoveReadingListArticle();
  const [likeId, setLikeId] = useState<string | null>(null);
  const { mutate: likeArticle, data: like, isLoading: isLikeLoading } = useLikeArticle();
  const { mutate: unlikeArticle, isLoading: isUnlikeLoading, isSuccess: isUnlikeSuccess } = useUnlikeArticle();
  const { data: likes } = useFetchLikes({
    enabled: canAccess,
  });

  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [isPrintLoading, setIsPrintLoading] = useState<boolean>(false);
  const wasPrintLoading = usePreviousValue(isPrintLoading);
  const { showToast } = useToast();
  const isPdf = content && containsPdfContent(content) && isPdfViewerSupported();

  // pdf articles have custom/different printing logic, therefore we need to manually call onAfterPrint
  useEffect(() => {
    if (!wasPrintLoading && isPrintLoading && isPdf) {
      setIsPrintLoading(false);
    }
  }, [isPrintLoading, wasPrintLoading, isPdf]);

  const isBookmarkLoading = isAddBookmarkLoading || isRemoveBookmarkLoading || isDefaultReadingListLoading;
  const bookmarkId = defaultReadingList
    ? defaultReadingList.articles.find((article) => article.articleId === id)?.id
    : undefined;

  const handlePrint = usePrint({
    title,
    contentRef: articlePrintRef,
    onAfterPrint: () => {
      setIsDownloadLoading(false);
      setIsPrintLoading(false);
    },
  });

  const onDownload = async () => {
    setIsDownloadLoading(true);
    if (isPdf) {
      await downloadPdfContent(content);
    } else if (articlePrintRef) {
      handlePrint();
      return;
    }
    setIsDownloadLoading(false);
  };

  const onPrint = () => {
    setIsPrintLoading(true);
    if (articlePrintRef && !isPdf) {
      handlePrint();
    } else if (isPdf) {
      printPdf();
    }
  };

  const onShare = async (): Promise<void> => {
    try {
      await copyToClipboard([{ link: `${window.location.origin}${ROUTES.article(id)}`, title }]);
      showToast('Link copied to clipboard');
    } catch {
      throw new Error('Failed to copy.');
    }
  };

  const onBookmark = () => {
    if (defaultReadingList) {
      if (bookmarkId) {
        const payload: RemoveReadingListArticlePayload = {
          readingListId: defaultReadingList.id,
          readingListItemId: bookmarkId,
        };
        removeBookmark(payload);
      } else {
        const payload: AddReadingListArticlePayload = {
          readingListId: defaultReadingList.id,
          article: {
            articleId: id,
            publishDate,
            title,
            tags: tags.map((tag) => tag.tagId),
          },
        };
        addBookmark(payload, {
          onSuccess: () => {
            showToast('Saved to My Content');
          },
        });
      }
    }
  };

  // This handler is only for active tab. Other tabs executes code from useInitBroadcastChannels
  const onLikeToggleSuccess = (likeId: string | null) => {
    setLikeId(likeId);
    Channels.likeChannel.postMessage(likeId);
    queryClient.invalidateQueries([queryKeys.fetchLikes.all]);
  };

  useEffect(() => {
    if (likes) {
      const likeId = likes.find((like) => like.contentId === String(id))?.id || '';
      setLikeId(likeId);
    }
  }, [likes, id]);

  useEffect(() => {
    if (like) {
      onLikeToggleSuccess(like.id);
    }
  }, [like]);

  useEffect(() => {
    if (isUnlikeSuccess) {
      onLikeToggleSuccess(null);
    }
  }, [isUnlikeSuccess]);

  const onLike = () => (likeId ? unlikeArticle(likeId) : likeArticle(String(id)));

  return {
    onShare,
    onBookmark,
    isBookmarkLoading,
    bookmarkId,
    onLike,
    isLikeLoading: isLikeLoading || isUnlikeLoading,
    likeId,
    onDownload,
    isDownloadLoading,
    onPrint,
    isPrintLoading,
  };
};
export default useGetArticleActions;
